import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import {
  subscribePaymentMethods,
  type PaymentMethodListResult,
} from '@/lib/firestoreTenantDetail';

/**
 * Tela `/billing/methods/:tenantId`: meios de pagamento salvos do tenant
 * (somente leitura).
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 00:22 +0200 (Sprint 16 —
 * rotas /billing/* + sub-menu Cobrança).
 *
 * Contrato: `subscribePaymentMethods` (listener, sem ordenação nem limite);
 * mostra tipo, status, bandeira, final do cartão e se é o padrão. Sem
 * callable, sem escrita. Sem `tenantId` na URL o estado fica em `LOADING`
 * (a rota sempre traz o parâmetro).
 *
 * Usado por: `routes/router.tsx` (rota `billing/methods/:tenantId`, dentro
 * do `RequireSuperAdmin`); sem item de menu — único link de entrada é o
 * botão da `BillingOverviewPage`, que também não tem entrada no menu. Só
 * `apps/admin` (web). Spec: `docs/screens/billing-payment-methods.md`.
 *
 * Arquitetura: regra `match /paymentMethods/{methodId}` sob
 * `tenants/{tenantId}` (Lê: membro ou super-admin). Em 2026-10-10 nenhuma
 * Function grava essa coleção (comentário da regra, #796) — a lista tende a
 * vir vazia.
 *
 * Dados: `tenants/{tid}/paymentMethods/{id}` (`PaymentMethodSchema`):
 *   { "type": "card", "status": "ACTIVE", "is_default": true,
 *     "card_brand": "visa", "card_last4": "4242", "provider": "stripe",
 *     "provider_method_id": "pm_000000000000" }
 * `type` ∈ manual | card | pix | boleto | sepa_debit | …; `status` ∈
 * ACTIVE | EXPIRED | INVALID; `card_*` `null` fora de cartão.
 *
 * Falhas conhecidas:
 *  - #795 (WEB-1): o caminho errado (sob `billing/`) derrubou a tela
 *    irmã do `apps/app`; aqui a coleção é irmã de `payments`.
 *  - Doc fora do schema é descartado sem log nem report (#926).
 *
 * Issues: #795, #926, #796.
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function PaymentMethodsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { tenantId } = useParams<{ tenantId: string }>();
  const [state, setState] = useState<PaymentMethodListResult>({ kind: 'LOADING' });

  useEffect(() => {
    if (!tenantId) return;
    setState({ kind: 'LOADING' });
    return subscribePaymentMethods(tenantId, setState);
  }, [tenantId]);

  const methods = state.kind === 'READY' ? state.methods : [];

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={tenantId ?? '—'}>
          {t('billing.paymentMethods.title')}
        </Header>
      }
    >
      {state.kind === 'LOADING' && (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      )}
      {state.kind === 'ERROR' && <Alert type="error">{t('billing.paymentMethods.errorLoad')}</Alert>}
      {state.kind === 'READY' && (
        <Table
          items={methods}
          trackBy="id"
          variant="container"
          empty={
            <Box textAlign="center" color="inherit">
              {t('billing.paymentMethods.empty')}
            </Box>
          }
          columnDefinitions={[
            {
              id: 'type',
              header: t('billing.paymentMethods.columns.type'),
              cell: (m): React.ReactNode => (
                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{m.type}</span>
              ),
            },
            {
              id: 'status',
              header: t('billing.paymentMethods.columns.status'),
              cell: (m): React.ReactNode => m.status,
            },
            {
              id: 'brand',
              header: t('billing.paymentMethods.columns.brand'),
              cell: (m): React.ReactNode => m.card_brand ?? '—',
            },
            {
              id: 'last4',
              header: t('billing.paymentMethods.columns.last4'),
              cell: (m): React.ReactNode => (
                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{m.card_last4 ?? '—'}</span>
              ),
            },
            {
              id: 'default',
              header: t('billing.paymentMethods.columns.default'),
              cell: (m): React.ReactNode => (m.is_default ? '✓' : '—'),
            },
          ]}
        />
      )}
    </ContentLayout>
  );
}
